/**
 * Frekvenční strop a čítače vrstev — PRAVDA JE NA KLIENTOVI.
 *
 * Prvostranný, neidentifikující zápis (jen id kampaní a časy, žádný e-mail
 * ani visitor_id), jehož jediným účelem je neotravovat téhož člověka
 * opakovaně. Funkční úložiště, ne trackovací — smí bez souhlasu s analytikou
 * (stávající `NewsletterPopup` dělal totéž s `zooshopik.popup.v1`).
 *
 * Privátní režim / zakázané úložiště: vše spadne do paměti na dobu stránky
 * (fail-open — vrstva se pak může ukázat znovu, což je přijatelnější než ji
 * neukázat vůbec; stejná volba jako v `tracking/ids.ts`).
 */
import type { LayerDelivery, LayerFrequency } from './types';

const STORE_KEY = 'zooshopik.layers.v1';
const SESSION_KEY = 'zooshopik.layers.session.v1';
const LEGACY_POPUP_KEY = 'zooshopik.popup.v1';

/** Kolik časů zobrazení si u kampaně pamatujeme (víc není k ničemu). */
const MAX_SHOWN = 20;

/* -------------------------------------------------- globální strop pop-upů */

/** Nejvýš 1 pop-up za page-view. */
export const MAX_POPUPS_PER_PAGE = 1;
/** Nejvýš 2 pop-upy za session. */
export const MAX_POPUPS_PER_SESSION = 2;

/* ---------------------------------------------------------------- tvary */

interface LayerRecord {
  shown: string[];
  lastShownAt: string | null;
  closedAt: string | null;
  submittedAt: string | null;
  evergreenEndsAt: string | null;
}

interface PersistentState {
  v: 1;
  importedLegacy: boolean;
  layers: Record<string, LayerRecord>;
  counters: {
    visits: number;
    lastVisitAt: string | null;
    productViews: string[];
  };
}

interface SessionState {
  pagesVisited: number;
  productViews: number;
  popupsShown: number;
  shownIds: string[];
}

function emptyPersistent(): PersistentState {
  return {
    v: 1,
    importedLegacy: false,
    layers: {},
    counters: { visits: 0, lastVisitAt: null, productViews: [] },
  };
}

function emptySession(): SessionState {
  return { pagesVisited: 0, productViews: 0, popupsShown: 0, shownIds: [] };
}

function emptyRecord(): LayerRecord {
  return { shown: [], lastShownAt: null, closedAt: null, submittedAt: null, evergreenEndsAt: null };
}

/* -------------------------------------------------------- načtení/uložení */

let memoryPersistent: PersistentState | null = null;
let memorySession: SessionState | null = null;

function loadPersistent(): PersistentState {
  if (memoryPersistent !== null) return memoryPersistent;
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null && (parsed as PersistentState).v === 1) {
        const state = parsed as PersistentState;
        // Doplnit chybějící pole (starší tvar / ruční úprava).
        state.layers ??= {};
        state.counters ??= { visits: 0, lastVisitAt: null, productViews: [] };
        state.counters.productViews ??= [];
        memoryPersistent = state;
        return state;
      }
    }
  } catch {
    // Spadne na prázdný stav.
  }
  memoryPersistent = emptyPersistent();
  return memoryPersistent;
}

function savePersistent(state: PersistentState): void {
  memoryPersistent = state;
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch {
    // Jen paměť – přežije do zavření záložky.
  }
}

function loadSession(): SessionState {
  if (memorySession !== null) return memorySession;
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null) {
        const state = { ...emptySession(), ...(parsed as Partial<SessionState>) };
        state.shownIds ??= [];
        memorySession = state;
        return state;
      }
    }
  } catch {
    // Spadne na prázdný stav.
  }
  memorySession = emptySession();
  return memorySession;
}

function saveSession(state: SessionState): void {
  memorySession = state;
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // Jen paměť.
  }
}

function recordFor(state: PersistentState, layerId: string): LayerRecord {
  const existing = state.layers[layerId];
  if (existing !== undefined) {
    existing.shown ??= [];
    return existing;
  }
  const fresh = emptyRecord();
  state.layers[layerId] = fresh;
  return fresh;
}

/* --------------------------------------------------------- veřejné API */

/**
 * Zvýší čítač návštěv (repeat_visit) — jednou za page-view při mountu hosta.
 * Session čítač stránek se hlídá zvlášť přes `notePageView`.
 */
export function noteVisit(): void {
  const state = loadPersistent();
  state.counters.visits += 1;
  state.counters.lastVisitAt = new Date().toISOString();
  savePersistent(state);
}

/** Zaznamená zobrazení stránky do session i klouzavého okna (pages_visited). */
export function notePageView(): void {
  const session = loadSession();
  session.pagesVisited += 1;
  saveSession(session);
}

/** Zaznamená prohlídku produktu (product_views_count) do session i visitoru. */
export function noteProductView(): void {
  const session = loadSession();
  session.productViews += 1;
  saveSession(session);

  const state = loadPersistent();
  state.counters.productViews.push(new Date().toISOString());
  // Ať pole neroste bez omezení – 90 dní je nejdelší okno.
  const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
  state.counters.productViews = state.counters.productViews
    .filter((iso) => new Date(iso).getTime() >= cutoff)
    .slice(-500);
  savePersistent(state);
}

/** Počet návštěv (repeat_visit). */
export function visitCount(): number {
  return loadPersistent().counters.visits;
}

/** Počet prohlídek produktů v daném rozsahu. */
export function productViewCount(scope: 'session' | 'visitor', windowDays: number): number {
  if (scope === 'session') return loadSession().productViews;
  const cutoff = Date.now() - windowDays * 24 * 60 * 60 * 1000;
  return loadPersistent().counters.productViews.filter((iso) => new Date(iso).getTime() >= cutoff)
    .length;
}

/** Počet zobrazených stránek v daném rozsahu (visitor = celé okno session dat). */
export function pageViewCount(scope: 'session' | 'visitor'): number {
  // Napříč zařízeními/dny výloha stránky nepočítá (nemá klíč); pro `visitor`
  // vracíme stejné session číslo — je to poctivá aproximace (per zařízení).
  void scope;
  return loadSession().pagesVisited;
}

/**
 * Zapíše zobrazení vrstvy. U pop-upů zvedne i globální session čítač.
 * Evergreen odpočet se založí při PRVNÍM zobrazení a dál se jen čte.
 */
export function recordShown(layer: LayerDelivery, evergreenDurationSeconds?: number): void {
  const state = loadPersistent();
  const record = recordFor(state, layer.id);
  const now = new Date().toISOString();
  record.shown.push(now);
  if (record.shown.length > MAX_SHOWN) record.shown = record.shown.slice(-MAX_SHOWN);
  record.lastShownAt = now;
  if (record.evergreenEndsAt === null && evergreenDurationSeconds !== undefined) {
    record.evergreenEndsAt = new Date(Date.now() + evergreenDurationSeconds * 1000).toISOString();
  }
  savePersistent(state);

  if (layer.surface === 'popup') {
    const session = loadSession();
    session.popupsShown += 1;
    if (!session.shownIds.includes(layer.id)) session.shownIds.push(layer.id);
    saveSession(session);
  }
}

export function recordClosed(layerId: string): void {
  const state = loadPersistent();
  recordFor(state, layerId).closedAt = new Date().toISOString();
  savePersistent(state);
}

export function recordSubmitted(layerId: string): void {
  const state = loadPersistent();
  recordFor(state, layerId).submittedAt = new Date().toISOString();
  savePersistent(state);
}

/** Konec evergreen odpočtu pro tuto vrstvu (nebo null, když ještě neběžel). */
export function evergreenEndsAt(layerId: string): string | null {
  return loadPersistent().layers[layerId]?.evergreenEndsAt ?? null;
}

/** Kolik pop-upů se v této session už ukázalo (globální strop). */
export function popupsShownThisSession(): number {
  return loadSession().popupsShown;
}

/**
 * Splňuje vrstva frekvenční strop? (klientská polovina — server může jen
 * ubírat, nikdy nepřidává; rozpor se řeší průnikem v hostu).
 */
export function passesFrequency(layer: LayerDelivery, now: number = Date.now()): boolean {
  const freq: LayerFrequency = layer.frequency;
  const state = loadPersistent();
  const record = state.layers[layer.id];

  // Odeslání formuláře zháší VŽDY (odběratel už nemá dostávat popup na odběr).
  if (record?.submittedAt != null) return false;

  switch (freq.mode) {
    case 'always':
      return true;

    case 'once':
      // Jakékoli zobrazení, zavření či odeslání = natrvalo zhasnuto.
      return record == null || (record.shown.length === 0 && record.closedAt == null);

    case 'once_per_session': {
      const session = loadSession();
      return !session.shownIds.includes(layer.id);
    }

    case 'every_n_days': {
      if (record?.lastShownAt == null) return true;
      const days = Math.max(1, freq.days ?? 7);
      const elapsed = now - new Date(record.lastShownAt).getTime();
      return elapsed >= days * 24 * 60 * 60 * 1000;
    }

    case 'max_n_times': {
      if (record == null) return true;
      // Zavření zháší natrvalo (stejně jako u once).
      if (record.closedAt != null) return false;
      const limit = Math.max(1, freq.count ?? 1);
      const relevant =
        freq.days == null
          ? record.shown
          : record.shown.filter(
              (iso) => now - new Date(iso).getTime() < (freq.days ?? 0) * 24 * 60 * 60 * 1000,
            );
      return relevant.length < limit;
    }

    default:
      return true;
  }
}

/**
 * Jednorázový import staršího stavu uvítacího popupu. Když seedovaná kampaň
 * nese `legacyKey` a ten klíč v úložišti existuje, založí jí syntetický
 * záznam zobrazení s časem z legacy klíče — kdo popup zavřel, nedostane ho
 * znovu. Proběhne jen jednou (příznak `importedLegacy`).
 */
export function importLegacyState(layers: readonly LayerDelivery[]): void {
  const state = loadPersistent();
  if (state.importedLegacy) return;

  let legacyValue: string | null = null;
  try {
    legacyValue = window.localStorage.getItem(LEGACY_POPUP_KEY);
  } catch {
    legacyValue = null;
  }

  if (legacyValue !== null) {
    const when = isoOrNow(legacyValue);
    for (const layer of layers) {
      if (layer.legacyKey === LEGACY_POPUP_KEY) {
        const record = recordFor(state, layer.id);
        if (record.shown.length === 0) {
          record.shown.push(when);
          record.lastShownAt = when;
          record.closedAt = when; // starý popup se choval jako „once" po zavření
        }
      }
    }
  }

  state.importedLegacy = true;
  savePersistent(state);
}

function isoOrNow(value: string): string {
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? new Date(time).toISOString() : new Date().toISOString();
}
